// A reference drawn as a chip: the thing's icon and its current name, opening it.
// One component for every place a reference is drawn: texts with references
// (`RefText`), the document editor's reference node and the document's static
// view (`StaticRefChips`).
//
// Promise:
// - The name is the current one (resolved through `refQuery`); until it
//   arrives, the name as written is shown.
// - A reference the viewer cannot see (gone, hidden), a title link (`[[name]]`,
//   no id) and one of a kind this app has no screen for are struck through and
//   do not open. They keep the name as written, and say why on hover or focus.
//   Nothing is lost: the text keeps the kind and id as written.
// - How a kind is drawn (icon, where it opens) is what the parts registered as
//   their favorite kinds (`defineScreenPart({ favoriteKinds })`); a kind may
//   draw its own mark instead of the icon (`chipMark`: a person's picture).
// - Inside text being written, the caller decides with `opensOn` whether a
//   click opens the chip or only selects it (a plain click must stay free to
//   select the chip there).
// - On a screen with a side view (the Agent chat), a plain click opens the
//   thing there when its kind has one; other clicks follow the link.
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { m } from "@trameria/i18n";
import { useApi, useMeApp } from "../app/context";
import { useOpenInSide } from "../shell/side-view";
import { Icon } from "../ui/icon";
import { Tooltip } from "../ui/tooltip";
import { refQuery } from "./queries";

// Colored text with no background or frame, flowing and wrapping with the
// text around it. The long press shows nothing of the system's own (no link
// preview).
const CHIP = "[-webkit-touch-callout:none]";

export interface RefChipProps {
  /** null for a title link (`[[name]]`). */
  kind: string | null;
  id: string | null;
  /** The name as written in the text. */
  label: string;
  /** Whether this press opens the reference (default: always). */
  opensOn?: (e: { metaKey: boolean; ctrlKey: boolean }) => boolean;
}

export function RefChip({ kind, id, label, opensOn }: RefChipProps) {
  if (kind === null || id === null || id === "")
    return <StruckChip label={label.trim()} why={m.refs_not_found()} />;
  return <KnownChip kind={kind} id={id} label={label} opensOn={opensOn} />;
}

function KnownChip({
  kind,
  id,
  label,
  opensOn,
}: {
  kind: string;
  id: string;
  label: string;
  opensOn?: RefChipProps["opensOn"];
}) {
  const { favoriteKinds, extensions } = useMeApp();
  const openInSide = useOpenInSide();
  const view = favoriteKinds[kind];
  // A Hub's desktop names the workspaces' things from the cloud.
  const elsewhere = !view && extensions.workspace !== undefined;
  const ref = useQuery({
    ...refQuery(useApi(), kind, id),
    enabled: view !== undefined || elsewhere,
  });
  const written = label.trim() || `${kind}:${id}`;
  if (!view) {
    const r = elsewhere ? ref.data : undefined;
    if (r?.visible)
      return (
        <ElsewhereChip label={r.label || written} why={m.refs_in_cloud()} />
      );
    if (r && !r.visible && r.unreachable)
      return (
        <ElsewhereChip label={written} why={m.refs_unreachable()} unreachable />
      );
    return <StruckChip label={written} why={m.refs_cannot_open_here()} />;
  }
  if (ref.data && !ref.data.visible)
    return <StruckChip label={written} why={m.refs_not_found()} />;
  const now = ref.data?.visible ? ref.data : null;
  const KindIcon = view.icon;
  const Mark = view.chipMark;
  const name = now?.label || written;
  return (
    <Link
      to={view.to(now?.id ?? id, now?.path)}
      data-ref={`${kind}:${id}`}
      draggable={false}
      onClick={(e) => {
        // Prevented: the router does not follow it (the click selects instead).
        if (opensOn && !opensOn(e)) e.preventDefault();
        else openInSide(e, kind, now?.id ?? id);
      }}
      className={`${CHIP} cursor-pointer text-accent hover:underline`}
    >
      {Mark ? (
        <Mark id={now?.id ?? id} name={name} />
      ) : (
        <KindIcon className="mr-[3px] inline-block h-[0.85em] w-[0.85em] align-[-0.08em]" />
      )}
      {name}
    </Link>
  );
}

/** A thing that is there but opens elsewhere (or is out of reach now): named, not struck. */
function ElsewhereChip({
  label,
  why,
  unreachable = false,
}: {
  label: string;
  why: string;
  unreachable?: boolean;
}) {
  return (
    <Tooltip text={why}>
      <button
        type="button"
        aria-disabled="true"
        data-ref-elsewhere=""
        aria-label={m.refs_missing_label({ label, why })}
        className={`${CHIP} cursor-default text-muted`}
      >
        {unreachable && (
          <Icon
            kind="unreachable"
            className="mr-[3px] inline-block h-[1em] w-[1em] align-[-0.12em]"
          />
        )}
        {label}
      </button>
    </Tooltip>
  );
}

function StruckChip({ label, why }: { label: string; why: string }) {
  const shown = label || why;
  return (
    <Tooltip text={why}>
      {/* A button that does nothing: it can take the focus, so the reason can be read from the keyboard. */}
      <button
        type="button"
        aria-disabled="true"
        data-ref-missing=""
        aria-label={m.refs_missing_label({ label: shown, why })}
        // The line has the text's color: a fainter line was not visible on
        // phones in the light theme.
        className={`${CHIP} cursor-default text-muted line-through`}
      >
        {shown}
      </button>
    </Tooltip>
  );
}
